import { createSlice } from '@reduxjs/toolkit';
import { type SettingsState, type MobileAppConfig, DEFAULT_MOBILE_APP_CONFIG } from './mobileConfigTypes';
import { fetchSystemSettings, updateSystemSettings } from './mobileConfigThunks';
import { logoutUser } from '@/features/auth/authThunks';

const initialState: SettingsState = {
  settings: null,
  isLoading: false,
  isSaving: false,
  error: null,
  hasFetched: false,
  hydratedFromDefaults: false,
};

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    clearSettingsState: () => {
      return { ...initialState };
    },
    clearSettingsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ---------------------------
      // fetchSystemSettings
      // ---------------------------
      .addCase(fetchSystemSettings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSystemSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.settings = action.payload.settings;
        state.hydratedFromDefaults = action.payload.hydratedFromDefaults;
        state.hasFetched = true;
      })
      .addCase(fetchSystemSettings.rejected, (state, action) => {
        state.isLoading = false;
        const payload = action.payload as { message?: string } | undefined;
        state.error = payload?.message || action.error.message || 'Failed to fetch settings';
        state.hydratedFromDefaults = false;
        state.hasFetched = true;
      })

      // ---------------------------
      // updateSystemSettings
      // ---------------------------
      .addCase(updateSystemSettings.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(updateSystemSettings.fulfilled, (state, action) => {
        state.isSaving = false;

        const { updates } = action.meta.arg; // typed as UpdateSettingsRequest

        if (!state.settings) {
          // First-time: initialize with defaults + incoming updates (so missing keys exist)
          state.settings = {
            ...DEFAULT_MOBILE_APP_CONFIG,
            ...updates,
          } as MobileAppConfig;
        } else {
          // Normal case: patch only the provided keys
          state.settings = {
            ...state.settings,
            ...updates,
          };
        }

        state.error = null;
        state.hydratedFromDefaults = false;
      })
      .addCase(updateSystemSettings.rejected, (state, action) => {
        state.isSaving = false;
        const payload = action.payload as { message?: string } | undefined;
        state.error = payload?.message || action.error.message || 'Failed to update setting';
      })

      // ---------------------------
      // logout
      // ---------------------------
      .addCase(logoutUser.fulfilled, () => {
        return { ...initialState };
      });
  },
});

export const { clearSettingsState, clearSettingsError } = settingsSlice.actions;
export default settingsSlice.reducer;
